import { useEffect, useState } from "react";
import { Save, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useDashboard } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/PageState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const SIZES = ["1-10 employees", "11-50 employees", "51-100 employees", "101-250 employees", "251-500 employees", "500+ employees"];
const TYPES = ["Sole Proprietorship", "Partnership", "Private Limited", "LLP", "Public Limited"];

const FIELDS = [
  { key: "business_name", label: "Business name", type: "text", placeholder: "Your company name" },
  { key: "industry", label: "Industry", type: "text", placeholder: "e.g. Auto components manufacturing" },
  { key: "business_type", label: "Business type", type: "select", options: TYPES },
  { key: "org_size", label: "Approximate organisation size", type: "select", options: SIZES },
  { key: "geography", label: "Geography", type: "text", placeholder: "e.g. Pune, Maharashtra" },
  { key: "business_model", label: "Primary business model", type: "text", placeholder: "e.g. B2B components supplier" },
  { key: "products_services", label: "Main products / services", type: "textarea", placeholder: "What you make or offer" },
  { key: "tech_environment", label: "Current technology environment", type: "textarea", placeholder: "e.g. Tally, spreadsheets, WhatsApp" },
  { key: "objective", label: "Primary transformation objective", type: "textarea", placeholder: "What outcome matters most in the next year" },
];

export default function BusinessProfile() {
  const { data, refresh } = useDashboard();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const isDemo = data?.is_demo;

  useEffect(() => {
    api.get("/organisation").then(({ data }) => setForm(data.organisation.profile || {})).catch(() => setForm({}));
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/organisation/profile", form);
      toast.success("Business profile saved");
      refresh?.();
    } catch (e) {
      toast.error("Couldn't save profile", { description: e.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-2xl" /></div>;

  return (
    <div>
      <PageHeader
        eyebrow="Onboarding"
        title="Business Profile"
        description="Tell ORBIT about your business. This context shapes your assessment and every recommendation."
      >
        {!isDemo && (
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
          </Button>
        )}
      </PageHeader>

      {isDemo && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="demo-readonly-banner">
          <Lock className="h-4 w-4 shrink-0" />
          This is the demonstration company (Aarohan Precision Components) and is read-only. Create your own account to build a profile.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 rounded-2xl border border-border bg-card p-6 sm:p-8 md:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key} className={`space-y-1.5 ${f.type === "textarea" ? "md:col-span-2" : ""}`}>
            <Label htmlFor={f.key}>{f.label}</Label>
            {f.type === "text" && (
              <Input id={f.key} disabled={isDemo} value={form[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} placeholder={f.placeholder} data-testid={`profile-${f.key}`} />
            )}
            {f.type === "textarea" && (
              <Textarea id={f.key} disabled={isDemo} rows={3} value={form[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} placeholder={f.placeholder} data-testid={`profile-${f.key}`} />
            )}
            {f.type === "select" && (
              <Select disabled={isDemo} value={form[f.key] || ""} onValueChange={set(f.key)}>
                <SelectTrigger data-testid={`profile-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
